/**
 * Permission Change Notifications Component
 * Epic #17: Real-time Permission Update Notifications
 *
 * Displays toast notifications for permission changes and critical permission revocation modal
 */

import React, { useEffect, useState, useCallback } from 'react';
import { ToastNotification, Modal, Button, InlineNotification, Layer } from '@carbon/react';
import { CheckmarkFilled, WarningAlt, Information, ErrorFilled } from '@carbon/icons-react';
import websocketService, { type PermissionChangeEvent } from '../../services/websocketService';
import { authService } from '../../services/authService';
import styles from './PermissionChangeNotifications.module.scss';

interface PermissionChangeNotificationsProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  autoConnect?: boolean;
}

interface NotificationState {
  id: string;
  event: PermissionChangeEvent;
  timestamp: Date;
}

export const PermissionChangeNotifications: React.FC<PermissionChangeNotificationsProps> = ({
  position = 'top-right',
  autoConnect = true,
}) => {
  const [notifications, setNotifications] = useState<NotificationState[]>([]);
  const [criticalModal, setCriticalModal] = useState<{
    open: boolean;
    event?: PermissionChangeEvent;
  }>({ open: false });
  const [isConnected, setIsConnected] = useState(false);

  /**
   * Handle permission change event from WebSocket
   */
  const handlePermissionChange = useCallback((eventData: PermissionChangeEvent) => {
    console.log('[PermissionNotifications] ========== WEBSOCKET EVENT RECEIVED ==========');
    console.log('[PermissionNotifications] Full event data:', JSON.stringify(eventData, null, 2));
    console.log('[PermissionNotifications] Event type:', eventData.type);
    console.log('[PermissionNotifications] Event changes:', JSON.stringify(eventData.changes, null, 2));
    console.log('[PermissionNotifications] Event message:', eventData.message);
    console.log('[PermissionNotifications] ===================================================');

    const notification: NotificationState = {
      id: `${eventData.userId}-${Date.now()}`,
      event: eventData,
      timestamp: new Date(),
    };

    // Show critical modal for critical permission revocations
    if (eventData.type === 'critical_revoked') {
      console.log('[PermissionNotifications] 🚨 CRITICAL_REVOKED EVENT TRIGGERED - SHOWING MODAL');
      console.log('[PermissionNotifications] Roles removed:', eventData.changes?.roles_removed);
      console.log('[PermissionNotifications] Critical flag:', eventData.changes?.critical);
      setCriticalModal({
        open: true,
        event: eventData,
      });
    }

    // Add to notification list
    setNotifications(prev => {
      const updated = [...prev, notification];
      // Keep only last 5 notifications
      return updated.slice(-5);
    });

    // Auto-remove non-critical notifications after 5 seconds
    if (eventData.type !== 'critical_revoked') {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }, 5000);
    }

    // Trigger page reload for affected resources
    if (eventData.changes.affected_resources && eventData.changes.affected_resources.length > 0) {
      setTimeout(() => {
        handleAutoReload(eventData);
      }, 3000); // Give user time to read the notification
    }
  }, []);

  /**
   * Auto-reload logic for affected pages
   */
  const handleAutoReload = (event: PermissionChangeEvent) => {
    const currentPath = window.location.pathname;
    const affectedResources = event.changes.affected_resources || [];

    // Map resource types to paths
    const resourcePathMapping: Record<string, string[]> = {
      admin: ['/admin', '/platform-admin', '/rbac'],
      system_config: ['/admin/settings', '/admin/config'],
      student_data: ['/students', '/dashboard/family'],
      analytics: ['/analytics', '/reports'],
      curriculum: ['/curriculum', '/courses'],
      content: ['/content', '/educational-content'],
    };

    // Check if current page is affected
    const shouldReload = affectedResources.some(resource => {
      const paths = resourcePathMapping[resource] || [];
      return paths.some(path => currentPath.startsWith(path));
    });

    if (shouldReload) {
      console.log('[PermissionNotifications] Reloading page due to permission change');

      // Show a brief notification before reload
      setNotifications(prev => [
        ...prev,
        {
          id: `reload-${Date.now()}`,
          event: {
            type: 'permission_updated',
            userId: event.userId,
            changes: {},
            timestamp: new Date().toISOString(),
            message: 'Refreshing page to apply permission changes...',
          },
          timestamp: new Date(),
        },
      ]);

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  };

  /**
   * Connect to permission updates WebSocket
   */
  useEffect(() => {
    if (!autoConnect) return;

    const token = authService.getToken();
    if (!token) {
      console.warn('[PermissionNotifications] No auth token available');
      return;
    }

    // Register component with WebSocket service
    const componentId = 'permission-notifications';
    websocketService.registerComponent(componentId, ['permission_change', 'connected', 'disconnected', 'error']);

    // Connect to permission updates
    websocketService.connectToPermissionUpdates(token);

    // Listen for permission change events
    websocketService.on('permission_change', handlePermissionChange);

    // Listen for connection status
    websocketService.on('connected', () => {
      console.log('[PermissionNotifications] WebSocket connected');
      setIsConnected(true);
    });

    websocketService.on('disconnected', () => {
      console.log('[PermissionNotifications] WebSocket disconnected');
      setIsConnected(false);
    });

    websocketService.on('error', error => {
      console.error('[PermissionNotifications] WebSocket error:', error);
    });

    return () => {
      // Cleanup listeners
      websocketService.off('permission_change', handlePermissionChange);
      websocketService.cleanupListeners(componentId);
    };
  }, [autoConnect, handlePermissionChange]);

  /**
   * Get notification kind based on event type
   */
  const getNotificationKind = (type: PermissionChangeEvent['type']): 'success' | 'warning' | 'error' | 'info' => {
    switch (type) {
      case 'role_added':
        return 'success';
      case 'role_removed':
        return 'warning';
      case 'critical_revoked':
        return 'error';
      case 'permission_updated':
      default:
        return 'info';
    }
  };

  /**
   * Get notification title based on event type
   */
  const getNotificationTitle = (type: PermissionChangeEvent['type']): string => {
    switch (type) {
      case 'role_added':
        return 'New Permissions Granted';
      case 'role_removed':
        return 'Permissions Removed';
      case 'critical_revoked':
        return 'Critical Permissions Revoked';
      case 'permission_updated':
      default:
        return 'Permissions Updated';
    }
  };

  /**
   * Get position CSS class name based on position prop
   */
  const getPositionClassName = (): string => {
    const baseClass = styles.notificationContainer;

    switch (position) {
      case 'top-right':
        return `${baseClass} ${styles.notificationContainerTopRight}`;
      case 'top-left':
        return `${baseClass} ${styles.notificationContainerTopLeft}`;
      case 'bottom-right':
        return `${baseClass} ${styles.notificationContainerBottomRight}`;
      case 'bottom-left':
        return `${baseClass} ${styles.notificationContainerBottomLeft}`;
      default:
        return `${baseClass} ${styles.notificationContainerTopRight}`;
    }
  };

  /**
   * Handle critical modal close
   */
  const handleCriticalModalClose = () => {
    setCriticalModal({ open: false });

    // Navigate to home or appropriate page
    window.location.href = '/';
  };

  return (
    <>
      {/* Toast Notifications */}
      <div className={getPositionClassName()}>
        {notifications.map(notification => (
          <ToastNotification
            key={notification.id}
            kind={getNotificationKind(notification.event.type)}
            title={getNotificationTitle(notification.event.type)}
            subtitle={notification.event.message || ''}
            caption={new Date(notification.event.timestamp).toLocaleTimeString()}
            timeout={notification.event.type === 'critical_revoked' ? 0 : 5000}
            onClose={() => {
              setNotifications(prev => prev.filter(n => n.id !== notification.id));
            }}
            lowContrast
          />
        ))}
      </div>

      {/* Critical Permission Revocation Modal */}
      <Modal
        open={criticalModal.open}
        onRequestClose={handleCriticalModalClose}
        modalHeading="Critical Permissions Revoked"
        primaryButtonText="Return to Home"
        secondaryButtonText="Stay on Page"
        onRequestSubmit={handleCriticalModalClose}
        onSecondarySubmit={() => setCriticalModal({ open: false })}
        danger
        preventCloseOnClickOutside
      >
        <div className={styles.modalSection}>
          <InlineNotification
            kind="error"
            title="Access Restricted"
            subtitle="Your access to critical features has been revoked."
            lowContrast
            hideCloseButton
          />
        </div>

        <p className={styles.modalText}>
          {criticalModal.event?.message ||
            'Critical permissions have been revoked by an administrator. Some features may no longer be accessible.'}
        </p>

        {criticalModal.event?.changes.roles_removed && criticalModal.event.changes.roles_removed.length > 0 && (
          <div className={styles.modalSection}>
            <strong>Roles Removed:</strong>
            <ul className={styles.modalList}>
              {criticalModal.event.changes.roles_removed.map((role, index) => (
                <li key={index}>{role}</li>
              ))}
            </ul>
          </div>
        )}

        {criticalModal.event?.changes.affected_resources &&
          criticalModal.event.changes.affected_resources.length > 0 && (
            <div>
              <strong>Affected Areas:</strong>
              <ul className={styles.modalList}>
                {criticalModal.event.changes.affected_resources.map((resource, index) => (
                  <li key={index}>{resource.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</li>
                ))}
              </ul>
            </div>
          )}
      </Modal>

      {/* Connection Status Indicator (only in dev) */}
      {process.env.NODE_ENV === 'development' && (
        <div className={styles.connectionStatus} data-connected={isConnected}>
          Permission WS: {isConnected ? 'Connected' : 'Disconnected'}
        </div>
      )}
    </>
  );
};

export default PermissionChangeNotifications;
